import { useId, type CSSProperties, type Ref } from "react";
import type { InputTargetConversation } from "@/api/conversation-targets";
import { Spinner } from "@/components/ui";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import {
  inputTargetsErrorMessage,
  type InputTargets,
} from "@/hooks/use-input-targets";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import { InputTargetPickerOptions } from "./input-target-picker-options";

/** The real target menu presentation, also used by inert guide recordings. */
export function InputTargetPickerSurface({
  ref,
  rows,
  showNew,
  selectedIndex,
  standalone = false,
  local = false,
  popoverScope,
  disabled = false,
  status = "ready",
  mode = "recent",
  error,
  style,
  className,
  nativeSurface = true,
  testId = "input-target-picker",
  onPickNew,
  onPickRow,
}: {
  ref?: Ref<HTMLDivElement>;
  rows: InputTargetConversation[];
  showNew: boolean;
  selectedIndex: number;
  standalone?: boolean;
  local?: boolean;
  popoverScope?: "tray";
  disabled?: boolean;
  status?: InputTargets["status"];
  mode?: InputTargets["mode"];
  error?: string | null;
  style?: CSSProperties;
  className?: string;
  nativeSurface?: boolean;
  testId?: string;
  onPickNew?: () => void;
  onPickRow?: (row: InputTargetConversation) => void;
}) {
  const t = useT(inputBarPickersMessages);
  const headingId = useId();
  const hasRows = rows.length > 0;
  return (
    <div
      ref={ref}
      className={cn(
        surfaceRoleClassName("menu"),
        "absolute !overflow-y-auto",
        className,
      )}
      style={style}
      data-testid={testId}
      data-tray-popover={popoverScope === "tray" || undefined}
      data-interactive={nativeSurface ? "true" : undefined}
      {...surfaceRoleAttributes(
        "menu",
        nativeSurface
          ? {
              nativeLevel: "root",
              nativeGroup: "menus",
            }
          : {},
      )}
    >
      {!standalone && !local ? (
        <div
          className={cn(
            "px-2.5 py-1.5",
            hasRows && "border-b border-glass-stroke-subtle",
          )}
        >
          <span
            id={headingId}
            className="text-appkit-mini font-medium uppercase tracking-wide text-muted"
          >
            {mode === "recent" ? t("recent") : t("results")}
          </span>
        </div>
      ) : null}
      {showNew || hasRows ? (
        <InputTargetPickerOptions
          rows={rows}
          showNew={showNew}
          selectedIndex={selectedIndex}
          standalone={standalone}
          disabled={disabled}
          labelledBy={standalone || local ? undefined : headingId}
          onPickNew={onPickNew}
          onPickRow={onPickRow}
        />
      ) : null}
      {status === "loading" && !hasRows ? (
        <div className="flex justify-center px-2.5 pb-2 pt-0.5">
          <Spinner
            className="h-3.5 w-3.5"
            data-testid="input-target-picker-loading"
          />
        </div>
      ) : null}
      {(status === "ready" && !hasRows) || (local && !showNew) ? (
        <div className="px-2.5 pb-2 pt-0.5">
          <span className="text-appkit-mini text-muted">
            {local
              ? t("noCommandsMatch")
              : mode === "recent"
                ? t("noRecentTargets")
                : t("noTargetsMatch")}
          </span>
        </div>
      ) : null}
      {status === "error" ? (
        <p
          role="alert"
          className="px-2.5 pb-2 pt-0.5 text-appkit-mini text-danger"
        >
          {inputTargetsErrorMessage()}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="px-2.5 pb-2 pt-0.5 text-appkit-mini text-danger"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
